import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StyledButton from '../../common/StyledButton';
import BoothSelectionMap from '../BoothSelectionMap';
import api from '../../../services/api';

const STATUS_POLL_MS = 30000;

const formatCountdown = (seconds) => {
  if (typeof seconds !== 'number' || seconds <= 0) return null;
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
};

const BoothSelection = ({ onNext, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [selectedBooths, setSelectedBooths] = useState([]);
  const [mapData, setMapData] = useState([]);
  const [registrationDialog, setRegistrationDialog] = useState(false);
  const [needsElectricity, setNeedsElectricity] = useState(false);

  // Server-side soft holds carried over from a previous in-progress
  // session. We surface them to the vendor so they can either resume or
  // explicitly release them before grabbing different booths.
  const [existingHolds, setExistingHolds] = useState([]);
  const [secondsUntilExpiry, setSecondsUntilExpiry] = useState(null);
  const [releasing, setReleasing] = useState(false);

  const refreshHolds = useCallback(async () => {
    try {
      const { data } = await api.get('/vendor/registration-status');
      const list = Array.isArray(data) ? data : [];
      const target = list[0];
      if (!target) {
        setExistingHolds([]);
        setSecondsUntilExpiry(null);
        return;
      }
      const holds = (target.registrations || []).filter(
        (r) => r.raw_registration_status === 'soft_held'
      );
      setExistingHolds(holds);
      setSecondsUntilExpiry(target.soonest_seconds_until_expiry ?? null);
    } catch {
      // Silent: this is a status lookup, not a critical path.
    }
  }, []);

  useEffect(() => {
    refreshHolds();
    const id = setInterval(refreshHolds, STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [refreshHolds]);

  const handleReleaseAll = async () => {
    if (!existingHolds.length) return;
    setReleasing(true);
    try {
      await api.post('/vendor/registration/abandon');
      setExistingHolds([]);
      setSecondsUntilExpiry(null);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "We couldn't release that booth. Refresh and try again — if it's still stuck, you can ignore it; the hold will expire automatically."
      );
    } finally {
      setReleasing(false);
    }
  };

  useEffect(() => {
    const fetchBooths = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('Fetching booth data...');
        const { data } = await api.get('/map');
        console.log('Received booth data:', {
          isArray: Array.isArray(data),
          length: data?.length,
          firstFewItems: data?.slice(0, 3),
          data
        });
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received from server');
        }
        
        setMapData(data);
      } catch (err) {
        console.error('Error fetching booth data:', {
          error: err,
          response: err.response,
          message: err.message
        });
        setError(
          err.response?.data?.error ||
            "We couldn't load the booth map. Check your internet connection and refresh the page."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBooths();
  }, []);

  const handleBoothSelect = (boothData) => {
    // Check if this booth is already selected
    if (selectedBooths.some(booth => booth.id === boothData.id)) {
      setError(
        'This booth is already in your selection — remove it from the list below if you want to pick a different one.'
      );
      return;
    }

    // Check if we've reached the limit of 3 booths
    if (selectedBooths.length >= 3) {
      setError(
        'You can only select up to 3 booths during initial registration. You can request more after admin approval.'
      );
      return;
    }

    setSelectedBooth(boothData);
    setRegistrationDialog(true);
  };

  const handleRegistrationSubmit = () => {
    const newBooth = {
      ...selectedBooth,
      needs_electricity: needsElectricity,
      price: calculateBoothPrice(selectedBooth)
    };
    setSelectedBooths([...selectedBooths, newBooth]);
    setRegistrationDialog(false);
    setSelectedBooth(null);
    setNeedsElectricity(false);
    setError('');
  };

  const handleRemoveBooth = (boothId) => {
    setSelectedBooths(selectedBooths.filter(booth => booth.id !== boothId));
  };

  const handleContinue = () => {
    if (selectedBooths.length === 0) {
      setError('Please select at least one booth by tapping a green square on the map.');
      return;
    }
    if (onNext) {
      onNext(selectedBooths);
    }
  };

  const calculateBoothPrice = (booth) => {
    if (!booth) return 0;
    let basePrice = booth.is_corner ? 80 : 70; // $80 for corner, $70 for regular
    if (needsElectricity) basePrice += 5; // Add $5 for electricity
    return basePrice;
  };

  const calculateTotalPrice = () => {
    return selectedBooths.reduce((total, booth) => total + booth.price, 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Select Your Booth(s)</Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Browsing the map doesn't lock anything yet. Once you click <strong>Continue</strong>, your booth(s) will be held for you for <strong>60 minutes</strong>. Stay active in the form to keep them. The hold becomes firm once you submit for admin approval, but is not guaranteed until payment is complete.
      </Alert>

      {existingHolds.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleReleaseAll}
              disabled={releasing}
            >
              Release {existingHolds.length === 1 ? 'booth' : 'booths'}
            </Button>
          }
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2">
              You currently have {existingHolds.length} booth
              {existingHolds.length === 1 ? '' : 's'} held from an earlier session
              {formatCountdown(secondsUntilExpiry)
                ? ` (about ${formatCountdown(secondsUntilExpiry)} left)`
                : ''}
              :
            </Typography>
            {existingHolds.map((h) => (
              <Chip key={h.registration_id} size="small" label={`Booth ${h.booth_number || h.booth_id}`} />
            ))}
          </Stack>
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 3, border: '2px solid #ff9800' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          ⚠️ IMPORTANT: NO REFUNDS POLICY
        </Typography>
        <Typography variant="body2">
          <strong>All booth payments are final and non-refundable.</strong> Once payment is processed, no refunds will be issued under any circumstances, including but not limited to weather conditions, personal emergencies, or changes in plans. Please ensure you are committed to participating before completing your payment.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selectedBooths.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle1" gutterBottom>Selected Booths:</Typography>
          <List dense>
            {selectedBooths.map((booth) => (
              <ListItem key={booth.id}>
                <ListItemText
                  primary={`Booth ${booth.cell_name}`}
                  secondary={`${booth.is_corner ? 'Corner' : 'Regular'} Booth - ${booth.needs_electricity ? 'With' : 'No'} Electricity - $${booth.price}`}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleRemoveBooth(booth.id)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 2, borderTop: '1px solid #ddd', pt: 2 }}>
            <Typography variant="subtitle1">
              Total Price: ${calculateTotalPrice()}
            </Typography>
          </Box>
        </Paper>
      )}

      {!loading && !error && Array.isArray(mapData) && mapData.length > 0 && (
        <>
          <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#fafbfc' }}>
            <Typography variant="body2" color="text.secondary">
              Click on an available booth to select it. Blue booths are available, grey booths are taken.
              {selectedBooths.length < 3 && ' You can select up to 3 booths.'}
            </Typography>
          </Paper>
          
          <Box sx={{ mb: 3 }}>
            <BoothSelectionMap 
              onSelect={handleBoothSelect}
              initialMapData={mapData}
              selectedBooths={selectedBooths}
            />
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <StyledButton onClick={onBack}>
              Back
            </StyledButton>
            <StyledButton 
              variant="contained" 
              onClick={handleContinue}
              disabled={selectedBooths.length === 0}
            >
              Continue
            </StyledButton>
          </Box>
        </>
      )}

      <Dialog
        open={registrationDialog}
        onClose={() => setRegistrationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Booth {selectedBooth?.cell_name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Booth Details:
            </Typography>
            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Booth Number: {selectedBooth?.cell_name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Location: {selectedBooth?.x_coordinate}, {selectedBooth?.y_coordinate}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Section: {selectedBooth?.section}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Corner Booth: {selectedBooth?.is_corner ? 'Yes' : 'No'}
              </Typography>
            </Box>
            <Box sx={{ mt: 3, mb: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={needsElectricity} onChange={e => setNeedsElectricity(e.target.checked)} color="primary" />}
                label="I need electricity at my booth (+$5)"
              />
            </Box>
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Estimated Fee: ${calculateBoothPrice(selectedBooth)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedBooth?.is_corner ? '• Corner booth ($80)' : '• Regular booth ($70)'}
                {needsElectricity && ' • Electricity (+$5)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                * Payment will be required after admin approval
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrationDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleRegistrationSubmit}
            disabled={!selectedBooth}
          >
            Add Booth
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoothSelection; 